import { useLocalizedText } from './localizedText';
import { TableProps } from '@amzn/awsui-components-react/polaris/table';

/**
 * Fixed width (px) for the Shift Name column, shared by the volunteer and coordinator shift tables.
 * Applied as `width`/`minWidth` on the column definition. Note: without `resizableColumns` the table
 * uses `table-layout: auto`, so `width` is a layout HINT rather than a hard pin — the 150px is
 * best-effort at some viewports. Enabling `resizableColumns` would hard-pin it but also make every
 * column user-drag-resizable, a broader user-facing change than intended here, so it stays off.
 */
export const SHIFT_NAME_COLUMN_WIDTH_PX = 150;

export type ColumnMap<T> = {
  [key: string]: (item: T) => JSX.Element | string;
};

export type ColumnData<T> = {
  columnDefinitions: TableProps.ColumnDefinition<T>[];
  columnDisplay: TableProps.ColumnDisplayProperties[];
};

export type EditableColConfig<T> = TableProps.ColumnDefinition<T>['editConfig'];

export function useTableColumns<T>(
  columnMap: ColumnMap<T>,
  tableName: string,
  namespaces: string[] = ['translation'],
  editableCols: Record<string, EditableColConfig<T>> | undefined = {},
  columnWidths: Record<string, number> = {}
): ColumnData<T> {
  const { t } = useLocalizedText(namespaces);

  return {
    columnDisplay: Object.keys(columnMap).map((key) => ({
      id: key,
      visible: true,
    })),
    columnDefinitions: Object.entries(columnMap).map((entry) => {
      const [key, mapValue] = entry;
      return {
        id: key,
        header: t(`${tableName}.columns.${key}`),
        editConfig:
          editableCols && Object.keys(editableCols).includes(key) ? editableCols[key] : undefined,
        width: columnWidths[key],
        minWidth: columnWidths[key],
        // This cast is required because Object.entries converts the value to unknown, even though it is defined in the ColumnMap type
        cell: mapValue as (item: T) => JSX.Element | string,
      };
    }),
  };
}
